/**
 * Hash Mentions TipTap Extension
 *
 * Simple # mentions functionality for channels and events.
 * Uses suggestions service directly - no global functions or complex orchestration.
 */

import Mention from '@tiptap/extension-mention'
import { VueRenderer } from '@tiptap/vue-3'
import tippy, { type Instance as TippyInstance } from 'tippy.js'

import { Z_INDEX } from '../../../../core-layer/constants'
import HashMentionsList from '../suggestions/HashMentionsList.vue'

import { hashMentionsPluginKey } from './mentionPluginKeys'

// TipTap components and popup references
let component: VueRenderer | null = null
let popup: TippyInstance | null = null

const getMentionPopupParent = () => {
  // Keep the popup inside the HighRise namespace so wrapper-scoped styles still apply.
  return document.querySelector<HTMLElement>('.membershipApp:not(.hr-detached-container)') ?? document.body
}

export const HashMentions = Mention.extend({
  name: 'hashMentions',
  priority: 1000, // High priority to handle keyboard before other extensions
}).configure({
  HTMLAttributes: {
    class: 'mention',
  },
  suggestion: {
    char: '#',
    pluginKey: hashMentionsPluginKey,

    // Items function - needs to return something for keyboard navigation to work
    items: async ({ query }: { query: string }) => {
      // Return a placeholder - the component handles actual display
      // But TipTap needs items for its internal keyboard handling
      return [{ id: 'loading', label: query }]
    },

    render: () => {
      return {
        onStart(props: any) {
          component = new VueRenderer(HashMentionsList, {
            props: {
              ...props,
              // Pass query to component so it can fetch its own data
              query: props.query || '',
            },
            editor: props.editor,
          })

          if (!props.clientRect) {
            return
          }

          if (!component.element) {
            return
          }

          popup = tippy(document.body, {
            appendTo: getMentionPopupParent,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            theme: 'cp-mention-suggestions',
            arrow: false,
            maxWidth: 'none',
            getReferenceClientRect: props.clientRect as any,
            zIndex: Z_INDEX.MENTION_SUGGESTIONS,
            popperOptions: {
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['top-start', 'bottom-start', 'right-start', 'left-start'],
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                  },
                },
              ],
            },
            hideOnClick: false,
            onShow(instance) {
              // Add mobile-specific event listeners to close on outside touch
              const handleOutsideTouch = (event: Event) => {
                const target = event.target as Element
                if (!instance.popper.contains(target)) {
                  instance.hide()
                }
              }

              // Add touchstart listener for mobile devices
              const outsideListenerTimeoutId = window.setTimeout(() => {
                document.addEventListener('click', handleOutsideTouch, {
                  passive: true,
                })
              }, 0)

              // Store cleanup function
              ;(instance as any)._outsideTouchHandler = handleOutsideTouch
              ;(instance as any)._outsideTouchTimeoutId = outsideListenerTimeoutId
            },
            onHide(instance) {
              // Clean up mobile event listeners
              if ((instance as any)._outsideTouchHandler) {
                document.removeEventListener('click', (instance as any)._outsideTouchHandler)
                delete (instance as any)._outsideTouchHandler
              }
              if ((instance as any)._outsideTouchTimeoutId) {
                window.clearTimeout((instance as any)._outsideTouchTimeoutId)
                delete (instance as any)._outsideTouchTimeoutId
              }
            },
          })
        },

        onUpdate(props: any) {
          if (component) {
            component.updateProps({
              ...props,
              query: props.query || '',
            })
          }

          if (!props.clientRect || !popup) {
            return
          }

          popup.setProps({
            getReferenceClientRect: props.clientRect as any,
          })
        },

        onBeforeUpdate() {
          return true
        },

        onKeyDown(props: any) {
          const { event } = props

          if (!component) {
            return false
          }

          const componentRef = (component as any).ref

          // Special handling for Enter - get selected item and call command
          if (event.key === 'Enter') {
            const selectedItem = componentRef?.getSelectedItem?.()
            if (selectedItem && props.command) {
              event.preventDefault()
              event.stopPropagation()
              props.command(selectedItem)
              return true
            }
          }

          // For other keys, forward to component
          if (componentRef?.handleKeyDown) {
            return componentRef.handleKeyDown(event)
          }

          return false
        },

        onExit() {
          try {
            if (component) {
              component.destroy()
              component = null
            }
          } catch (error) {
            console.warn('[HashMentions] Error destroying component:', error)
          }

          try {
            if (popup) {
              popup.destroy()
              popup = null
            }
          } catch (error) {
            console.warn('[HashMentions] Error removing popup:', error)
          }
        },
      }
    },

    command: ({ editor, range, props }: any) => {
      try {
        // Delete the # and query text
        editor.chain().focus().deleteRange(range).run()

        // Insert mention using unified mention node
        if (props.type === 'channel') {
          editor.commands.insertMention({
            id: props.id,
            label: props.label,
            name: props.label,
            slug: props.slug,
            type: 'channel',
            icon: props.icon,
            iconType: props.iconType,
          })
        } else if (props.type === 'event') {
          editor.commands.insertMention({
            id: props.id,
            label: props.label,
            name: props.label,
            type: 'event',
            timestamp: props.timestamp,
          })
        } else if (props.type === 'course') {
          editor.commands.insertMention({
            id: props.id,
            label: props.label,
            name: props.label,
            type: 'course',
          })
        }

        // Add space after mention
        editor.commands.insertContent(' ')
      } catch (error) {
        console.error('[HashMentions] Error in command:', error)
      }
    },
  },
})
